import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface DateTimePickerProps {
  /** Current value as an ISO 8601 string (empty string when unset). */
  value?: string;
  /** Called with the new ISO 8601 string whenever the date or time changes. */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Single date + time picker built from the shared Calendar, Popover, and Input
 * primitives. Use this anywhere a date-time needs to be selected — never a raw
 * `<input type="datetime-local">`.
 */
export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick a date & time",
  disabled,
  className,
  id,
}: DateTimePickerProps) {
  const parsed = value ? new Date(value) : undefined;
  const current =
    parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined;

  const handleDate = (picked?: Date) => {
    if (!picked) return;
    const base = current ?? new Date();
    const next = new Date(picked);
    next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    onChange(next.toISOString());
  };

  const handleTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const next = current ? new Date(current) : new Date();
    next.setHours(
      Number.parseInt(hours, 10) || 0,
      Number.parseInt(minutes, 10) || 0,
      0,
      0
    );
    onChange(next.toISOString());
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start pl-3 text-left font-normal",
            !current && "text-muted-foreground",
            className
          )}
        >
          {current ? format(current, "PPP 'at' p") : <span>{placeholder}</span>}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={current}
          onSelect={handleDate}
          initialFocus
        />
        <div className="border-t p-3">
          <label className="z-label mb-2 block text-muted-foreground">
            Time
          </label>
          <Input
            type="time"
            value={current ? format(current, "HH:mm") : ""}
            onChange={(event) => handleTime(event.target.value)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
